import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import { EventCard } from './EventCard';
import { FilterBar } from './FilterBar';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';
import { Pagination } from './Pagination';

interface NewsEvent {
  id: string;
  ts: string;
  ticker?: string;
  source?: string;
  title: string;
  summary?: string;
  link?: string;
  industry?: string;
  is_negative?: boolean;
  is_matched?: boolean;
  signal_change_pct?: number | null;
}

interface ExtendedEventsResponse {
  events: NewsEvent[];
  cursor: string | null;
}

export const EventFeed = () => {
  const [events, setEvents] = useState<NewsEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // 1. שואבים את הנתונים ישירות מה-URL במקום מ-useState
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTicker = searchParams.get('ticker') || '';
  const urlStart = searchParams.get('start_date') || '';
  const urlEnd = searchParams.get('end_date') || '';
  const urlTab = (searchParams.get('tab') as 'ALL' | 'MATCHED' | 'NEGATIVE') || 'ALL';

  // הסטייטים המקומיים עבור שדות ההקלדה (כדי שהמשתמש יוכל להקליד/לבחור)
  const [tickerInput, setTickerInput] = useState<string>(urlTicker);
  const [startDateInput, setStartDateInput] = useState<string>(urlStart);
  const [endDateInput, setEndDateInput] = useState<string>(urlEnd);
  
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isPaginationLoading, setIsPaginationLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 2. הדיבאונס: כשמקלידים טיקר, אחרי שנייה זה פשוט מעדכן את ה-URL
  useEffect(() => {
    const timerId = setTimeout(() => {
      if (tickerInput !== urlTicker) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          if (tickerInput) newParams.set('ticker', tickerInput);
          else newParams.delete('ticker');
          return newParams;
        });
      }
    }, 1000); // שנייה שלמה כמו שביקשת
    return () => clearTimeout(timerId);
  }, [tickerInput, urlTicker, setSearchParams]);

  // 3. הפונקציה המקורית שלך שלא שונתה (רק מקבלת את הפרמטרים מה-URL)
  const loadData = async (ticker?: string, startDate?: string, endDate?: string, currentTab?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let formattedStart: string | undefined = undefined;
      let formattedEnd: string | undefined = undefined;

      if (startDate) {
        const [year, month, day] = startDate.split('-').map(Number);
        formattedStart = new Date(year, month - 1, day, 0, 0, 0).toISOString(); 
      }      
      if (endDate) {
        const [year, month, day] = endDate.split('-').map(Number);
        formattedEnd = new Date(year, month - 1, day, 23, 59, 59).toISOString(); // סוף היום
      }      
      
      const isNegativeParam = currentTab === 'NEGATIVE' ? 'true' : undefined;
      
      const data = (await api.getEvents({
        ticker: ticker || undefined,
        start_date: formattedStart,
        end_date: formattedEnd,
        limit: 50,
        cursor: undefined,
        is_negative: isNegativeParam
      })) as ExtendedEventsResponse;
      
      setEvents(data.events || []); 
      setNextCursor(data.cursor || null); 
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load events. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // 4. בכל פעם שה-URL משתנה (מרענון או הקלדה), טוענים נתונים ומסנכרנים את השדות
  const prevTabRef = useRef(urlTab);

  useEffect(() => {
    // הקסם: אם לחצנו עכשיו על MATCHED (והיינו קודם בטאב אחר), ויש לנו כבר נתונים במערך
    if (urlTab === 'MATCHED' && prevTabRef.current !== 'MATCHED' && events.length > 0) {
      // לא קוראים ל-loadData! 
      // משאירים את הכתבות שיש בזיכרון כדי שהפרונטאנד יחתוך אותן בעצמו.
    } else {
      // רענון עמוד, הקלדת טיקר, סינון תאריך או מעבר ל-NEGATIVE - ימשיכו לעבוד מול השרת כרגיל!
      loadData(urlTicker, urlStart, urlEnd, urlTab);
    }

    // שומרים את הטאב הנוכחי בזיכרון לפעם הבאה ש-useEffect ירוץ
    prevTabRef.current = urlTab; 

    setTickerInput(urlTicker);
    setStartDateInput(urlStart);
    setEndDateInput(urlEnd);
  }, [urlTicker, urlStart, urlEnd, urlTab]);

  // 5. הכפתורים פשוט משנים את ה-URL למעלה
  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (startDateInput) newParams.set('start_date', startDateInput);
      else newParams.delete('start_date');
      if (endDateInput) newParams.set('end_date', endDateInput);
      else newParams.delete('end_date');
      return newParams;
    });
    setIsFilterOpen(false);
  };

  const handleClear = () => {
    setTickerInput('');
    setStartDateInput('');
    setEndDateInput('');
    setSearchParams({});
    setIsFilterOpen(false);
  };

  const handleTabChange = (tab: 'ALL' | 'MATCHED' | 'NEGATIVE') => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (tab === 'ALL') newParams.delete('tab');
      else newParams.set('tab', tab);
      return newParams;
    });
  };

  const handleLoadMore = async () => {
    if (!nextCursor || isPaginationLoading) return;
    setIsPaginationLoading(true);
    try {
      let formattedStart: string | undefined = undefined;
      let formattedEnd: string | undefined = undefined;

      if (urlStart) {
        const [year, month, day] = urlStart.split('-').map(Number);
        formattedStart = new Date(year, month - 1, day, 0, 0, 0).toISOString(); 
      }
      if (urlEnd) {
        const [year, month, day] = urlEnd.split('-').map(Number);
        formattedEnd = new Date(year, month - 1, day, 23, 59, 59).toISOString(); 
      }

      const isNegativeParam = urlTab === 'NEGATIVE' ? 'true' : undefined;
      
      const data = (await api.getEvents({
        ticker: urlTicker || undefined,
        start_date: formattedStart,
        end_date: formattedEnd,
        limit: 50,
        cursor: nextCursor || undefined,
        is_negative: isNegativeParam
      })) as ExtendedEventsResponse; 
      
      setEvents(prevEvents => [...prevEvents, ...(data.events || [])]);
      setNextCursor(data.cursor || null);
    } catch (error) {
      console.error("Error fetching more data:", error);
    } finally {
      setIsPaginationLoading(false);
    }
  };

  const displayedEvents = events.filter(event => {
    if (urlTab === 'MATCHED') {
      return event.is_matched === true;
    }   
    return true; 
  });

  const getDateRangeText = () => {
    if (urlStart && urlEnd) return `${urlStart} TO ${urlEnd}`;
    if (urlStart) return `FROM ${urlStart}`;
    if (urlEnd) return `UNTIL ${urlEnd}`;

    if (events.length > 0) {
      const oldestEvent = events[events.length - 1];
      const oldestDate = new Date(oldestEvent.ts);
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

      if (oldestDate >= twentyFourHoursAgo) {
        return 'LAST 24H';
      }

      const formattedDate = oldestDate.toLocaleDateString('en-GB', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
      
      return `SINCE ${formattedDate}`;
    }

    return 'LATEST EVENTS';
  };

  return (
    <div className="space-y-6">
      <div className="relative rounded-2xl overflow-hidden border border-amber-100 dark:border-[#1d2d47] bg-gradient-to-br from-amber-50 via-orange-50/60 to-white dark:from-[#0e1624] dark:via-[#1a1000]/50 dark:to-[#080c14] p-6">
        <div className="pointer-events-none absolute -top-8 -right-8 w-48 h-48 rounded-full bg-gradient-to-br from-[#f5a623] to-[#f97316] opacity-[0.08] dark:opacity-[0.12] blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-32 h-32 rounded-full bg-gradient-to-t from-[#f5476a] to-transparent opacity-[0.07] dark:opacity-[0.10] blur-2xl" />

        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-[22px] font-bold tracking-tight bg-gradient-to-r from-[#f5a623] via-[#f97316] to-[#f5476a] bg-clip-text text-transparent">
              Event Feed
            </h1>
            <p className="text-[13px] text-gray-500 dark:text-[#a3b6cc] mt-0.5">
              Company news & catalysts · source: Finnhub · most recent first
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-[#0e1624] border border-emerald-200 dark:border-[#10d98e]/30 shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500 dark:bg-[#10d98e]" />
              </span>
              <span className="text-[11px] font-semibold text-emerald-700 dark:text-[#10d98e] font-mono tracking-wide">LIVE</span>
            </div>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-[#0e1624] border border-gray-200 dark:border-[#1d2d47] text-[11px] font-semibold text-gray-600 dark:text-[#c2d0e0] hover:border-amber-300 dark:hover:border-[#f5a623]/40 transition-colors"
            >
              Filter
            </button>
          </div>
        </div>

        {!isLoading && (
          <div className="relative flex items-center gap-2.5 mt-4 flex-wrap">
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/80 dark:bg-[#0e1624]/80 border border-gray-100 dark:border-[#1d2d47] shadow-sm">
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-gray-900 dark:text-[#f1f5fb] leading-none">
                  {events.length} 
                </div>
                <div className="text-[10px] text-gray-400 dark:text-[#a3b6cc] mt-0.5 uppercase tracking-wide font-semibold">
                  {getDateRangeText()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#10d98e]/8 dark:bg-[#10d98e]/10 border border-[#10d98e]/20">
              <span className="w-2 h-2 rounded-full bg-[#10d98e] shrink-0" />
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-[#10d98e] leading-none">
                  {events.filter(e => e.is_matched).length}
                </div>
                <div className="text-[10px] text-[#10d98e]/70 mt-0.5 uppercase tracking-wide font-semibold">Matched</div>
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[#f5476a]/8 dark:bg-[#f5476a]/10 border border-[#f5476a]/20">
              <span className="w-2 h-2 rounded-full bg-[#f5476a] shrink-0" />
              <div>
                <div className="text-[18px] font-bold font-mono tabular-nums text-[#f5476a] leading-none">
                  {events.filter(e => e.is_negative).length}
                </div>
                <div className="text-[10px] text-[#f5476a]/70 mt-0.5 uppercase tracking-wide font-semibold">Negative</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 w-full">
        <div className="w-full min-w-0 rounded-2xl border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] overflow-hidden shadow-sm">
          <div className="h-[3px] bg-gradient-to-r from-[#f5a623] via-[#f97316] to-[#f5476a]" />

          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-[#1d2d47]">
            <div className="text-[11px] tracking-[0.05em] text-gray-400 dark:text-[#c2d0e0]">
              <span className="font-bold text-gray-900 dark:text-[#f1f5fb] uppercase mr-1.5">COMPANY NEWS</span>
              events table · most recent first
            </div>
            <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-[#080c14]/60 border border-gray-200 dark:border-[#1d2d47] rounded-xl p-1">
              {(['ALL', 'MATCHED', 'NEGATIVE'] as const).map((tab) => {
                const isActive = urlTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => handleTabChange(tab)}
                    className={`px-3 py-1 rounded-lg text-[11px] font-medium transition-all duration-150 whitespace-nowrap ${
                      isActive
                        ? tab === 'NEGATIVE'
                          ? 'bg-white dark:bg-[#141e30] text-red-600 dark:text-[#f5476a] shadow-sm'
                          : tab === 'MATCHED'
                          ? 'bg-white dark:bg-[#141e30] text-emerald-600 dark:text-[#10d98e] shadow-sm'
                          : 'bg-white dark:bg-[#141e30] text-gray-900 dark:text-[#f1f5fb] shadow-sm'
                        : 'text-gray-500 dark:text-[#c2d0e0] hover:text-gray-800 dark:hover:text-[#d5deea]'
                    }`}
                  >
                    {tab === 'ALL' ? 'All' : tab === 'MATCHED' ? 'Matched' : 'Negative'}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col px-5">
            {error ? (
              <ErrorState error={error} onRetry={() => 
                loadData(urlTicker, urlStart, urlEnd, urlTab)} />
            ) : isLoading ? (
              <LoadingState />
            ) : displayedEvents.length === 0 ? (
              <EmptyState onClear={handleClear} />
            ) : (
              displayedEvents.map(event => (
                <EventCard 
                  key={event.id} 
                  event={event} 
                />
              ))
            )}
            <Pagination 
              isLoading={isLoading} 
              isPaginationLoading={isPaginationLoading} 
              nextCursor={nextCursor} 
              onLoadMore={handleLoadMore} 
            />
          </div>
        </div>

        <div className="lg:sticky lg:top-[76px] self-start">
          <FilterBar 
            tickerInput={tickerInput} setTickerInput={setTickerInput}
            startDateInput={startDateInput} setStartDateInput={setStartDateInput}
            endDateInput={endDateInput} setEndDateInput={setEndDateInput}
            isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen}
            onFilterSubmit={handleFilter} onClear={handleClear}
            activeTab={urlTab} onTabChange={handleTabChange} 
          />
        </div>
      </div>
    </div>
  );
};